import { useState, useEffect, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import { FormModal, Input, TextArea, Select } from '@core/components/ui';
import { useGetSegmentationCodesQuery } from '../../api/basesApi';

/**
 * BaseFormModal Component
 *
 * Modal for creating or editing a dedicated knowledge base.
 * Uses FormModal and TextInput wrappers for consistent styling.
 *
 * @param {Object} props
 * @param {boolean} props.isOpen - Whether the modal is open
 * @param {Function} props.onClose - Callback when modal is closed
 * @param {Function} props.onSubmit - Callback when form is submitted
 * @param {Object} props.base - Knowledge base object for editing (null for creating)
 * @param {string} props.apiError - API error message to display
 */
export function BaseFormModal({ isOpen, onClose, onSubmit, base = null, apiError = null }) {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    base_type: 'COMPANY',
    code: '',
  });
  const [errors, setErrors] = useState({});

  // Fetch segmentation codes
  const { data: segmentationCodes, isLoading: isLoadingCodes } = useGetSegmentationCodesQuery();

  // Initialize form data when base changes
  useEffect(() => {
    if (base) {
      setFormData({
        name: base.name || '',
        description: base.description || '',
        base_type: base.base_type || 'COMPANY',
        code: base.code || '',
      });
    } else {
      setFormData({
        name: '',
        description: '',
        base_type: 'COMPANY',
        code: '',
      });
    }
    setErrors({});
  }, [base, isOpen]);

  const handleSubmit = useCallback(() => {
    // Validation
    const newErrors = {};
    if (!formData.name.trim()) {
      newErrors.name = t('knowledgeBases.form.nameRequired', 'Name is required');
    }
    if (!formData.base_type) {
      newErrors.base_type = t('knowledgeBases.form.baseTypeRequired', 'Type is required');
    }
    if (!formData.code) {
      newErrors.code = t('knowledgeBases.form.codeRequired', 'Code is required');
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    // Submit
    onSubmit(formData);
  }, [formData, t, onSubmit]);

  const title = useMemo(() =>
    base
      ? t('knowledgeBases.form.editTitle', 'Edit Knowledge Base')
      : t('knowledgeBases.form.createTitle', 'Create Knowledge Base'),
    [base, t]
  );

  return (
    <FormModal
      open={isOpen}
      onClose={onClose}
      onSubmit={handleSubmit}
      title={title}
      submitText={t('common.save', 'Save')}
      cancelText={t('common.cancel', 'Cancel')}
      size="lg"
    >
      <div className="space-y-6">
        {/* API Error Banner */}
        {apiError && (
          <div className="p-4 rounded-lg bg-error/10 border border-error/20">
            <p className="text-sm text-error">{apiError}</p>
          </div>
        )}

        {/* Base Type Radio Buttons */}
        <div>
          <label className="block text-sm font-medium text-default mb-3">
            {t('knowledgeBases.form.baseType', 'Segmentation Type')} *
          </label>
          <div className="flex gap-8">
            <label className="flex items-center cursor-pointer">
              <input
                type="radio"
                name="base_type"
                value="COMPANY"
                checked={formData.base_type === 'COMPANY'}
                onChange={(e) => setFormData((prev) => ({ ...prev, base_type: e.target.value, code: '' }))}
                className="mr-3"
              />
              <span className="text-sm text-default">
                {t('knowledgeBases.form.company', 'Company')}
              </span>
            </label>
            <label className="flex items-center cursor-pointer">
              <input
                type="radio"
                name="base_type"
                value="COLLECTIVE_AGREEMENT"
                checked={formData.base_type === 'COLLECTIVE_AGREEMENT'}
                onChange={(e) => setFormData((prev) => ({ ...prev, base_type: e.target.value, code: '' }))}
                className="mr-3"
              />
              <span className="text-sm text-default">
                {t('knowledgeBases.form.collectiveAgreement', 'Collective Agreement')}
              </span>
            </label>
          </div>
          {errors.base_type && (
            <p className="mt-1 text-sm text-error">{errors.base_type}</p>
          )}
        </div>

        {/* Code Dropdown */}
        <Select
          options={
            formData.base_type === 'COMPANY'
              ? (segmentationCodes?.companies || []).map((company) => ({
                  value: company.code,
                  label: `${company.code} - ${company.name}`,
                }))
              : (segmentationCodes?.collective_agreements || []).map((agreement) => ({
                  value: agreement.code,
                  label: `${agreement.code} - ${agreement.name}`,
                }))
          }
          value={formData.code}
          onChange={(e) => setFormData((prev) => ({ ...prev, code: e.target.value }))}
          placeholder={
            isLoadingCodes
              ? t('common.loading', 'Loading...')
              : formData.base_type === 'COMPANY'
                ? t('knowledgeBases.form.selectCompany', 'Select a company')
                : t('knowledgeBases.form.selectAgreement', 'Select an agreement')
          }
          disabled={isLoadingCodes}
          error={errors.code}
          required
        />

        {/* Name Input */}
        <Input
          label={t('knowledgeBases.form.name', 'Name')}
          value={formData.name}
          onChange={(value) => setFormData((prev) => ({ ...prev, name: value }))}
          error={errors.name}
          required
        />

        {/* Description Textarea */}
        <TextArea
          label={t('knowledgeBases.form.description', 'Description')}
          value={formData.description}
          onChange={(value) => setFormData((prev) => ({ ...prev, description: value }))}
          error={errors.description}
          rows={4}
        />
      </div>
    </FormModal>
  );
}

BaseFormModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  base: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    name: PropTypes.string,
    description: PropTypes.string,
  }),
  apiError: PropTypes.string,
};
